import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Lock } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-default)] px-4">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border-default)] p-8 sm:p-10 space-y-6"
      >
        <div className="text-center">
          <Lock size={24} className="mx-auto text-[var(--brand-gold)] mb-3" />
          <div className="text-xs uppercase tracking-[0.3em] text-[var(--accent-gold)] mb-2">
            Admin Access
          </div>
          <h1 className="font-headings text-3xl text-[var(--text-primary)]">
            tpg<span className="text-[var(--brand-gold)]">.</span>decants
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Sign in to manage the boutique.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">
              Email
            </label>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">
              Password
            </label>
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm"
            />
          </div>
        </div>

        {error && (
          <div data-testid="login-error" className="text-sm text-red-400 text-center">
            {error}
          </div>
        )}

        <button
          data-testid="login-submit"
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-[var(--brand-gold)] text-black font-medium tracking-wide hover:bg-[var(--brand-gold-hover)] transition-colors disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>

        <div className="text-center">
          <a
            href="/"
            className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] tracking-wider uppercase"
          >
            ← Back to shop
          </a>
        </div>
      </form>
    </div>
  );
}
